import React, { useState } from "react";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import { fabric } from "fabric";
import Select from "@mui/material/Select";
import Button from "@mui/material/Button";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";
import { updateSheetObjects } from "../../store/reducers/availableSheets";
import axios from "axios";
import { URLs } from "../../utils/urls";
import JSZip from "jszip";
import { setLoading } from "../../store/reducers/modalToggler";
import { showToast } from "../../store/reducers/notificationSlice";
import { resizePdfCanvas } from "../../utils/helpers";
import { CrownIcon } from "../../assets/icons";
import { triggerCheckout } from "../../utils/triggerCheckout";

const DownloadDesignModal = ({ closeModal }) => {
  const [fileType, setFileType] = useState("JPEG");
  const [fileSize, setFileSize] = useState("large");
  const dispatch = useDispatch();
  const { canvas } = useSelector((state) => state.canvas);
  const { sheets } = useSelector((state) => state.availableSheets);
  const activeSheet = useSelector((state) => state.activeSheet).sheet;
  const { isEditing, shareImages } = useSelector((state) => state.loginSlice);

  const downloadCanvasData = () => {
    const tmpCanvas = new fabric.StaticCanvas("");
    let promises = [];

    if (isEditing.type === "createCopy" || isEditing.type === "") {
      dispatch(setLoading(false));
      dispatch(
        showToast({
          message: `Before downloading, save the project first.`,
          type: "warning",
        })
      );

      throw new Error("you need to save the template ");
    }
    if (fileType === "JPEG") {
      const zip = new JSZip();
      dispatch(
        updateSheetObjects({
          sheetId: activeSheet.id,
          objectList: [...canvas.getObjects()],
        })
      );
      for (let ind = 0; ind < sheets.length; ind++) {
        promises[ind] = new Promise((resolve) => {
          generateImages(sheets[ind], tmpCanvas, resolve, true);
        });
      }
      Promise.all(promises).then(async (vals) => {
        let i = 0;
        for (const imageData of vals) {
          const { img } = imageData;
          const filename = `image_${i + 1}.${fileType}`;
          zip.file(filename, img.split("base64,")[1], { base64: true });
          i++;
        }
        zip.generateAsync({ type: "blob" }).then((content) => {
          const downloadLink = document.createElement("a");
          downloadLink.href = URL.createObjectURL(content);
          downloadLink.download = "images.zip";
          downloadLink.click();
        });
      });
    } else if (fileType === "PDF" || fileType === "Impose print") {
      dispatch(setLoading(true));
      const tmpCanvas = new fabric.StaticCanvas("");
      let promises = [];
      dispatch(
        updateSheetObjects({
          sheetId: activeSheet.id,
          objectList: [...canvas.getObjects()],
        })
      );
      for (let ind = 0; ind < sheets.length; ind++) {
        promises[ind] = new Promise((resolve) => {
          generateImages(sheets[ind], tmpCanvas, resolve);
        });
      }
      Promise.all(promises).then(async (vals) => {
        let url = `${URLs.GET_PDF}?logger=true`;

        if (fileType === "Impose print") {
          url = `${URLs.GET_PDF}?collage=true&collage_type=${fileSize}&logger=true`;
        }

        const data = { payload: vals, service_id: isEditing.id };
        const token = sessionStorage.getItem("auth_token");
        await axios
          .post(url, data, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          })
          .then(async (res) => {
            console.log("res", res);
            if (res.status === 200) {
              const fileUrl = res.data.data.data;
              console.log("fileUrl: ", fileUrl);
              const response = await fetch(fileUrl);
              console.log("Response status: ", response.status);
              const blob = await response.blob();
              console.log("Blob size: ", blob.size);
              let url = window.URL.createObjectURL(blob);
              let a = document.createElement("a");
              a.href = url;
              a.download = `assets.pdf`;
              a.click();
              closeModal(null);
              dispatch(setLoading(false));
            }
          })
          .catch((err) => {
            if (err?.response?.status === 402) {
              dispatch(
                showToast({
                  message: ` ${err?.response?.data?.message}`,
                  type: "info",
                })
              );
              triggerCheckout(isEditing, shareImages, dispatch);
              dispatch(setLoading(false));
            } else {
              console.error("Error saving data:");
              dispatch(
                showToast({
                  message: `Error while downloading PDF ${err?.response?.data?.message}`,
                  type: "error",
                })
              );
              console.log(err);
            }
          });
      });
    }
  };
  const creatImagesForDownload = (newSheet, canvas, resolve) => {
    let image = "";
    if (fileSize === "small") {
      image = canvas.toDataURL({
        format: "jpeg",
      });
    } else if (fileSize === "large") {
      image = canvas.toDataURL({
        format: "jpeg",
        multiplier: 3,
      });
    }
    resolve({ size: newSheet.size, img: image });
  };
  const generateImages = (newSheet, canvas, resolve, isJPEG = false) => {
    if (newSheet?.backgroundColor) {
      canvas.backgroundColor = newSheet.backgroundColor;
    }
    canvas._objects = [];
    if (
      newSheet.objects.length &&
      !(newSheet.objects[0] instanceof fabric.Object)
    ) {
      fabric.util.enlivenObjects(newSheet.objects, function (objects) {
        canvas.add(...objects);
      });
    } else {
      canvas._objects = [...newSheet.objects];
    }

    if (newSheet.backgroundImage)
      canvas.backgroundImage = newSheet.backgroundImage;
    else {
      canvas.backgroundImage = null;
    }
    canvas?.renderAll();
    resizePdfCanvas(newSheet.size.width, newSheet.size.height, canvas);
    if (isJPEG) {
      creatImagesForDownload(newSheet, canvas, resolve);
    } else {
      const svg = canvas.toSVG();
      resolve({ size: newSheet.size, svg });
    }
  };

  const handleFileSizeChange = (event) => {
    setFileSize(event.target.value);
  };

  const handleFileTypeChange = (event) => {
    setFileType(event.target.value);
    if (event.target.value === "PDF" || event.target.value === "JPEG") {
      setFileSize("large");
    } else if (event.target.value === "Impose print") {
      setFileSize("4");
    }
  };

  const fileSizeOptions = {
    JPEG: [
      // {value: 'small', label: `Small · ${Math.floor(canvas.height)} x ${Math.floor(canvas.width)} px`},
      {
        value: "large",
        label: `Large · ${Math.floor(canvas.height) * 3} x ${
          Math.floor(canvas.width) * 3
        } px`,
      },
    ],
    PDF: [
      // {value: 'small', label: 'Small'},
      { value: "large", label: "Large" },
    ],
    "Impose print": [
      { value: "4", label: "Impose 4up" },
      { value: "8", label: "Impose 8up" },
    ],
  };

  return (
    <Stack gap="30px">
      <Box sx={{ width: "100%" }}>
        <Typography sx={{ paddingY: 2 }}>File Type</Typography>
        <Select
          BackdropProps={{ invisible: false }}
          fullWidth
          value={fileType}
          onChange={handleFileTypeChange}
          sx={{
            overflow: "hidden",
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                boxShadow: "none",
              },
            },
            disablePortal: true,
            disableScrollLock: true,
            hideBackdrop: true,
          }}
        >
          <MenuItem value="JPEG">
            <Stack className={"download-option"}>
              <span>JPEG</span>
              <p className="testStyle">
                High quality image, good for most uses
              </p>
            </Stack>
          </MenuItem>
          <MenuItem value="PDF">
            <Stack className={"download-option"}>
              <span>
                PDF <CrownIcon />{" "}
              </span>
              <p className="testStyle">
                High quality document, good for large prints and sharing
              </p>
            </Stack>
          </MenuItem>
          <MenuItem value="Impose print">
            <Stack className={"download-option"}>
              <span>
                Impose print <CrownIcon />
              </span>
              <p className="testStyle">
                High quality document, good for large prints and sharing
              </p>
            </Stack>
          </MenuItem>
        </Select>
      </Box>
      <Stack justifyContent="space-between" direction="row" alignItems="center">
        <Typography>
          {fileType === "Impose print" ? `Impose print type` : "File Size"}
        </Typography>
        <Select
          size="small"
          value={fileSize}
          onChange={handleFileSizeChange}
          sx={{ color: "#00688c", minWidth: 140 }}
          MenuProps={{
            PaperProps: {
              sx: {
                boxShadow: "none",
              },
            },
            disablePortal: true,
            disableScrollLock: true,
            hideBackdrop: true,
          }}
        >
          {fileSizeOptions[fileType].map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Stack>
      <Button
        onClick={downloadCanvasData}
        size="large"
        width="100%"
        sx={{ textDecoration: "none", textTransform: "none" }}
        variant="contained"
        endIcon={<CloudDownloadIcon />}
      >
        Download
      </Button>
    </Stack>
  );
};
export default DownloadDesignModal;
